import {
  Column,
  Columns,
  Stack,
  TextField,
  Heading,
  Text,
  TextLink,
  Button,
  Box,
  Divider,
} from "@origin-digital/ods-core";
import React, { useState } from "react";
import { useField, useFormikContext } from "formik";
import { apiClientPublic } from "src/api/apiService";
import { EicRequestsMetaUnderSamePremise } from "./types";
import {
  mapAccountDetailsToFormValues,
  analyzeEicRequestStatuses,
} from "./helper";
import { ConnectionTypeEnum } from "src/components/Premises/types";

export type State = "VIC" | "NSW" | "QLD" | "SA" | "WA" | "TAS" | "NT" | "ACT";

export type AddressLookupResponse = {
  address?: string;
  service_identifier?: string;
  fee_scripting?: string;
  db_fee?: number;
  mp_fee?: number;
  connection_types?: string[];
  state?: State;
  eic_requests_meta_under_same_premise?: EicRequestsMetaUnderSamePremise;
};

interface EICFormPageOneProps {
  onContinue: () => void;
  setHasApiError: (hasApiError: boolean) => void;
  onAlreadySubmitted?: () => void;
  onAcceptedConnectionType?: (
    connectionType: ConnectionTypeEnum | null
  ) => void;
  onIsReadonlyFlow?: (isReadonlyFlow: boolean) => void;
}

const EICFormPageOne: React.FC<EICFormPageOneProps> = ({
  onContinue,
  setHasApiError,
  onAlreadySubmitted,
  onAcceptedConnectionType,
  onIsReadonlyFlow,
}) => {
  // Use Formik's useField hook for each field
  const [referenceNumberField, referenceNumberMeta, referenceNumberHelpers] =
    useField("referenceNumber");
  const [postcodeField, postcodeMeta, postcodeHelpers] = useField("postcode");
  const { values, validateForm, setTouched, setFieldValue } = useFormikContext<{
    referenceNumber: string;
    postcode: string;
    address: string;
  }>();
  const [loading, setLoading] = useState<boolean>(false);

  // Custom handlers to ensure only digits and enforce length limits
  const handleReferenceNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value.replace(/[^OCoc0-9]/g, "");
    if (value.length >= 8 && value.toUpperCase().startsWith("OC")) {
      referenceNumberHelpers.setValue(value);
    } else if (
      value.length < 8 &&
      (value === "" ||
        value === "O" ||
        value === "o" ||
        value.toUpperCase().startsWith("OC"))
    ) {
      referenceNumberHelpers.setValue(value);
    }
  };

  const handlePostcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/g, "").slice(0, 4);
    postcodeHelpers.setValue(value);
  };

  const handleContinueClick = async () => {
    // trigger validation and mark all fields as touched
    setTouched({
      referenceNumber: true,
      postcode: true,
    });

    const errors = await validateForm();

    // Check if validation passed
    if (Object.keys(errors).length > 0) {
      // Validation failed, don't proceed with API call
      return;
    }

    setLoading(true);

    try {
      const response = await apiClientPublic.get<AddressLookupResponse>(
        "/address/lookup",
        {
          params: {
            id: values.referenceNumber,
            postcode: values.postcode,
          },
        }
      );

      const {
        address,
        connection_types,
        state,
        eic_requests_meta_under_same_premise,
      } = response.data;

      // Set basic address lookup data
      setFieldValue("address", address);
      setFieldValue("connectionTypes", connection_types);
      setFieldValue("state", state);

      const eicMeta = eic_requests_meta_under_same_premise ?? [];

      // Early return if no EIC requests found
      if (eicMeta.length === 0) {
        throw new Error("No EIC requests found");
      }

      setHasApiError(false);

      // Analyze EIC status
      const { allAccepted, hasMixedStatuses, firstAcceptedRequest } =
        analyzeEicRequestStatuses(eicMeta);

      // 1. All requests accepted -> show submitted page
      if (allAccepted) {
        // All requests accepted -> show submitted page
        onAlreadySubmitted?.();
        onIsReadonlyFlow?.(false);
        onAcceptedConnectionType?.(null);
        return;
      }

      // 2. Mixed status -> Readonly flow
      if (hasMixedStatuses) {
        onIsReadonlyFlow?.(true);
        onAcceptedConnectionType?.(firstAcceptedRequest!.connection_type);

        // Mixed status -> populate form with accepted account details for readonly page
        const mappedValues = mapAccountDetailsToFormValues(
          firstAcceptedRequest!.account_details
        );

        // Batch update form values
        Object.entries(mappedValues).forEach(([key, value]) => {
          setFieldValue(key, value);
        });

        onContinue();
        return;
      }

      // 3. Edit flow
      onIsReadonlyFlow?.(false);
      onAcceptedConnectionType?.(null);
      onContinue();
    } catch {
      setHasApiError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack
      space={["xlarge", "xlarge"]}
      data-role="section"
      id="eic-form-page-one"
    >
      <Stack>
        <Heading variant="h3">Request for a new energy account</Heading>
        <Text>
          Use this form to provide your consent and accept the fees and charges
          for an Origin electricity or natural gas energy agreement and
          connection. To proceed with the connection, please enter the request
          ID and the postcode of the property in question. You can find these
          details in the email you’ve received
        </Text>
        <Text>
          If you did not request this work, please contact us ASAP on{" "}
          <TextLink href="tel:1300137932">1300 137 932</TextLink>.
        </Text>
      </Stack>

      <Columns collapseBelow="lg">
        <Column width="3/4">
          <Stack space="large">
            <Heading variant="h4">Request details</Heading>

            <Stack>
              <TextField
                id="referenceNumber"
                variant="outlined"
                label="Request ID"
                value={referenceNumberField.value}
                onChange={handleReferenceNumberChange}
                onBlur={referenceNumberField.onBlur}
                error={Boolean(
                  referenceNumberMeta.touched && referenceNumberMeta.error
                )}
                helperText={
                  <Columns>
                    <Column>
                      {referenceNumberMeta.touched && referenceNumberMeta.error}
                    </Column>
                    {/* <Column width="content">
                      {referenceNumberField.value.length}/9
                    </Column> */}
                  </Columns>
                }
                domProps={
                  {
                    autoComplete: "on",
                  } as React.HTMLAttributes<HTMLInputElement>
                }
              />
              <TextField
                id="postcode"
                variant="outlined"
                label="Postcode"
                value={postcodeField.value}
                onChange={handlePostcodeChange}
                onBlur={postcodeField.onBlur}
                error={Boolean(postcodeMeta.touched && postcodeMeta.error)}
                helperText={
                  <Columns>
                    <Column>
                      {postcodeMeta.touched && postcodeMeta.error}
                    </Column>
                    <Column width="content">
                      {postcodeField.value.length}/4
                    </Column>
                  </Columns>
                }
                domProps={
                  {
                    autoComplete: "on",
                  } as React.HTMLAttributes<HTMLInputElement>
                }
              />
            </Stack>
          </Stack>
        </Column>
      </Columns>

      <Divider />

      <Box display="flex" justifyContent="flex-end">
        <Button onClick={handleContinueClick} spinner={loading}>
          Continue
        </Button>
      </Box>
    </Stack>
  );
};

export default EICFormPageOne;
